import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn, initials } from '@/lib/utils'

/* ------------------------------------------------------------------ Card */
export function Card({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('rounded-xl border bg-surface shadow-soft', className)} {...props} />
}

export function CardHeader({
  title,
  description,
  action,
  className,
}: {
  title: React.ReactNode
  description?: React.ReactNode
  action?: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex items-start justify-between gap-4 border-b px-4 py-3', className)}>
      <div className="min-w-0">
        <h3 className="text-[14px] font-semibold tracking-tight">{title}</h3>
        {description && <p className="mt-0.5 text-[12.5px] text-muted-foreground">{description}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}

/* ------------------------------------------------------------------ Badge */
export const badgeVariants = cva(
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border font-medium leading-none',
  {
    variants: {
      tone: {
        ok: 'border-ok-border bg-ok-soft text-ok',
        warn: 'border-warn-border bg-warn-soft text-warn',
        info: 'border-info-border bg-info-soft text-info',
        danger: 'border-danger-border bg-danger-soft text-danger',
        neutral: 'border-neutral-border bg-neutral-soft text-muted-foreground',
        brand: 'border-transparent bg-brand-soft text-brand',
        accent: 'border-transparent bg-accent-soft text-accent',
      },
      size: { sm: 'h-5 px-2 text-[11px]', md: 'h-6 px-2.5 text-xs' },
    },
    defaultVariants: { tone: 'neutral', size: 'md' },
  },
)

export function Badge({
  className,
  tone,
  size,
  dot,
  children,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { dot?: boolean }) {
  return (
    <span className={cn(badgeVariants({ tone, size }), className)} {...props}>
      {dot && <span className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  )
}

/* ------------------------------------------------------------------ Skeleton */
export function Skeleton({ className, ...props }: React.ComponentProps<'div'>) {
  return <div aria-hidden="true" className={cn('skeleton rounded-md', className)} {...props} />
}

/* ------------------------------------------------------------------ Separator */
export function Separator({ className, vertical }: { className?: string; vertical?: boolean }) {
  return <div role="separator" className={cn(vertical ? 'w-px self-stretch' : 'h-px w-full', 'bg-border', className)} />
}

/* ------------------------------------------------------------------ Avatar */
export function Avatar({ name, className }: { name: string | null | undefined; className?: string }) {
  const hue = [...(name ?? '?')].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 0)
  return (
    <span
      aria-hidden="true"
      className={cn('grid size-8 shrink-0 place-items-center rounded-full text-[11px] font-semibold', className)}
      style={{ background: `oklch(0.93 0.04 ${hue})`, color: `oklch(0.38 0.08 ${hue})` }}
    >
      {initials(name)}
    </span>
  )
}

/* ------------------------------------------------------------------ Kbd */
export function Kbd({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        'inline-flex h-5 min-w-5 items-center justify-center rounded border border-b-2 bg-surface px-1 font-mono text-[10.5px] font-medium text-muted-foreground',
        className,
      )}
    >
      {children}
    </kbd>
  )
}

/* ------------------------------------------------------------------ Empty state */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: React.ReactNode
  title: React.ReactNode
  description?: React.ReactNode
  action?: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-12 text-center', className)}>
      {icon && (
        <div className="relative mb-4">
          <div className="absolute inset-0 -m-3 rounded-full bg-brand-soft blur-xl" />
          <div className="relative grid size-12 place-items-center rounded-2xl border bg-surface text-brand shadow-card [&_svg]:size-5">
            {icon}
          </div>
        </div>
      )}
      <h3 className="text-[15px] font-semibold tracking-tight">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-[13px] text-muted-foreground">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  )
}

/* ------------------------------------------------------------------ Callout */
export function Callout({
  tone = 'info',
  icon,
  title,
  children,
  className,
}: {
  tone?: 'info' | 'warn' | 'danger' | 'ok'
  icon?: React.ReactNode
  title?: React.ReactNode
  children?: React.ReactNode
  className?: string
}) {
  const tones = {
    info: 'border-info-border bg-info-soft text-info',
    warn: 'border-warn-border bg-warn-soft text-warn',
    danger: 'border-danger-border bg-danger-soft text-danger',
    ok: 'border-ok-border bg-ok-soft text-ok',
  }
  return (
    <div role={tone === 'danger' ? 'alert' : 'status'} className={cn('flex gap-3 rounded-xl border p-3.5 text-[13px]', tones[tone], className)}>
      {icon && <span className="mt-0.5 shrink-0 [&_svg]:size-4">{icon}</span>}
      <div className="min-w-0">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn('text-foreground/80', title && 'mt-0.5')}>{children}</div>}
      </div>
    </div>
  )
}
